import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner, faTrash} from '@fortawesome/pro-solid-svg-icons';

import {ErrorBanner} from '../../../shared/components';

interface IRiskDraft {
  problem: string;
  mitigation: string;
}

interface ITopRiskItem {
  id: number;
  name: string;
}

export interface ITopRiskRowProps {
  risk: ITopRiskItem;
  selection: any;
  saving: boolean;
  draft: IRiskDraft;
  icon: React.ReactNode;
  isDeleteConfirming: boolean;
  cardError?: string;
  canReset: boolean;
  isLoadingTopRisks: boolean;
  onRiskDraftChange: (
    riskId: number,
    field: keyof IRiskDraft,
    value: string,
  ) => void;
  onDeleteClick: (risk: ITopRiskItem, isDeleteConfirming: boolean) => void;
  onPersist: (risk: ITopRiskItem, draft: IRiskDraft) => void;
  onReset: (risk: ITopRiskItem) => void;
}

export const TopRiskRow: React.FC<ITopRiskRowProps> = ({
  risk,
  selection,
  saving,
  draft,
  icon,
  isDeleteConfirming,
  cardError,
  canReset,
  isLoadingTopRisks,
  onRiskDraftChange,
  onDeleteClick,
  onPersist,
  onReset,
}) => {
  const riskId = Number(risk.id);

  return (
    <article
      className={`top-risks-step__row${selection ? ' is-selected' : ''}`}
      role="listitem"
    >
      <div className="top-risks-step__risk-cell">
        <div className="top-risks-step__risk-heading">
          <span className="top-risks-step__row-icon">{icon}</span>
          <span className="top-risks-step__card-title">{risk.name}</span>
        </div>
        <span className="top-risks-step__card-summary">
          {selection
            ? 'Risk captured. Update details here if needed.'
            : 'Add problem and mitigation for this risk.'}
        </span>
        {saving ? (
          <span className="top-risks-step__card-spinner" role="status">
            <FontAwesomeIcon icon={faSpinner} spin />
          </span>
        ) : null}
      </div>

      <div className="top-risks-step__problem-cell">
        <label className="top-risks-step__field">
          <span className="top-risks-step__mobile-field-label">Problem</span>
          <textarea
            rows={3}
            value={draft.problem}
            onChange={(event) =>
              onRiskDraftChange(riskId, 'problem', event.target.value)
            }
            placeholder="Describe the issue for this risk"
            disabled={saving || isLoadingTopRisks}
          />
        </label>
      </div>

      <div className="top-risks-step__mitigation-cell">
        <label className="top-risks-step__field">
          <span className="top-risks-step__mobile-field-label">Mitigation</span>
          <textarea
            rows={3}
            value={draft.mitigation}
            onChange={(event) =>
              onRiskDraftChange(riskId, 'mitigation', event.target.value)
            }
            placeholder="How will you address this risk?"
            disabled={saving || isLoadingTopRisks}
          />
        </label>
      </div>

      <div className="top-risks-step__actions-cell">
        <ErrorBanner
          message={cardError}
          className="form-alert top-risks-step__row-error"
        />

        <div className="top-risks-step__row-actions">
          <div className="top-risks-step__row-actions-main">
            {selection ? (
              <button
                type="button"
                className={`top-risks-step__delete-icon-btn${
                  isDeleteConfirming ? ' is-confirming' : ''
                }`}
                onClick={() => onDeleteClick(risk, isDeleteConfirming)}
                disabled={saving || isLoadingTopRisks}
                title={
                  isDeleteConfirming
                    ? 'Click again to confirm delete'
                    : 'Delete risk'
                }
                aria-label={
                  isDeleteConfirming
                    ? `Confirm delete ${risk.name}`
                    : `Delete ${risk.name}`
                }
              >
                <FontAwesomeIcon
                  icon={saving && isDeleteConfirming ? faSpinner : faTrash}
                  spin={saving && isDeleteConfirming}
                />
              </button>
            ) : (
              <span
                className="top-risks-step__delete-icon-placeholder"
                aria-hidden="true"
              />
            )}
            <button
              type="button"
              className="money-btn money-btn--primary money-btn--sm top-risks-step__save-btn"
              onClick={() => onPersist(risk, draft)}
              disabled={saving || isLoadingTopRisks}
            >
              {selection ? 'Update' : 'Save'}
            </button>
          </div>
          <button
            type="button"
            className="top-risks-step__reset-link"
            onClick={() => onReset(risk)}
            disabled={!canReset}
          >
            Reset
          </button>
        </div>
      </div>
    </article>
  );
};
